import React, { useEffect, useState } from "react";
import { FileText, IndianRupee, Layers, ListChecks } from "lucide-react";
import { AES, enc } from "crypto-js";
import swal from "sweetalert";
import PremiumListPage, { StatusBadge } from "./PremiumListPage";
import { BASE_URL } from "../../screens/Dashboard/CommonApi";
import DocumentDetailLayout from "../documents/DocumentDetailLayout";
import { defaultDocumentConfigs, getDocumentNumber } from "../documents/documentHelpers";

const decryptUniqueId = () => {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  return AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8);
};

const money = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const getDeleteUrl = (endpoint, row, key) => {
  if (!endpoint) return null;
  const value = row[key];
  return endpoint.replace(":id", encodeURIComponent(String(value || "").replace(/\//g, "!")));
};

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const directArray = [
    value.data,
    value.records,
    value.rows,
    value.items,
    value.list,
    value.result,
    value.results,
    value.content,
  ].find(Array.isArray);

  if (directArray) return directArray;

  const nestedArray = Object.values(value).find(Array.isArray);
  if (nestedArray) return nestedArray;

  return [];
};

const PremiumResourcePage = ({
  navigation,
  title,
  subtitle,
  createRoute,
  createLabel,
  fetchEndpoint,
  fetchBody = {},
  dataPath = "responseMessage",
  deleteEndpoint,
  deleteMethod = "DELETE",
  deleteKey = "id",
  deleteBody = true,
  transformRows,
  columns,
  searchPlaceholder,
  totalKey,
  emptyTitle,
  documentType,
  documentConfig = {},
  renderEdit,
  renderDetail,
  handledActions = [],
  onDocumentAction,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const jwtToken = localStorage.getItem("jwtToken");

  const fetchRows = () => {
    fetch(BASE_URL + fetchEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decryptUniqueId(), ...fetchBody }),
    })
      .then((response) => response.json())
      .then((data) => {
        const rawRows = normalizeRows(dataPath ? data?.[dataPath] : data);
        const nextRows = transformRows ? transformRows(rawRows) : rawRows;
        setRows(normalizeRows(nextRows));
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${title}:`, error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const handleDelete = deleteEndpoint
    ? (row) => {
        if (!window.confirm(`Are you sure? Once deleted, you will not be able to recover this ${title.toLowerCase()} record!`)) {
          return;
        }

        const requestBody = deleteBody ? JSON.stringify({ uniqueId: decryptUniqueId(), id: row[deleteKey] }) : undefined;
        fetch(BASE_URL + getDeleteUrl(deleteEndpoint, row, deleteKey), {
          method: deleteMethod,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: requestBody,
        })
          .then((response) => {
            if (response.ok) {
              setRows((current) => current.filter((item) => item[deleteKey] !== row[deleteKey]));
              swal({ icon: "success", title: `${title} Deleted Successfully` });
            }
          })
          .catch((error) => console.error(`Error deleting ${title}:`, error));
      }
    : undefined;

  const handleEditCancel = () => {
    setEditingDocument(null);
    fetchRows();
  };

  const handleDetailCancel = () => {
    setSelectedDocument(null);
    fetchRows();
  };

  const handleDocumentAction = (action, row) => {
    if (action === "Edit" && renderEdit) {
      setEditingDocument(row);
      return;
    }

    if (onDocumentAction) {
      onDocumentAction(action, row, {
        refresh: fetchRows,
        closeDetail: () => setSelectedDocument(null),
      });
    }
  };

  if (loading) {
    return <div className="premium-empty-state">Loading {title.toLowerCase()}...</div>;
  }

  const safeRows = normalizeRows(rows);
  const amount = totalKey ? safeRows.reduce((sum, row) => sum + Number(row[totalKey] || 0), 0) : 0;
  const rowsWithSerial = safeRows.map((row, index) => ({ ...row, serial: index + 1 }));
  const resolvedDocumentConfig = documentType
    ? {
        ...(defaultDocumentConfigs[documentType] || {}),
        title,
        amount: totalKey || documentConfig.amount,
        ...documentConfig,
        documentType,
      }
    : null;

  if (editingDocument && renderEdit) {
    return renderEdit(editingDocument, handleEditCancel);
  }

  if (selectedDocument && renderDetail) {
    return renderDetail(selectedDocument, handleDetailCancel);
  }

  if (selectedDocument && resolvedDocumentConfig) {
    const selectedKey = getDocumentNumber(selectedDocument, resolvedDocumentConfig);
    const latestSelected =
      rowsWithSerial.find((row) => getDocumentNumber(row, resolvedDocumentConfig) === selectedKey) || selectedDocument;
    const supportedActions = new Set([
      "Print",
      "Download PDF",
      ...(renderEdit ? ["Edit"] : []),
      ...handledActions,
    ]);
    const actionableConfig = {
      ...resolvedDocumentConfig,
      actions: (resolvedDocumentConfig.actions || []).filter((action) => supportedActions.has(action)),
    };

    return (
      <DocumentDetailLayout
        rows={rowsWithSerial}
        selectedRow={latestSelected}
        config={actionableConfig}
        onSelect={setSelectedDocument}
        onBack={handleDetailCancel}
        onAction={handleDocumentAction}
      />
    );
  }

  return (
    <PremiumListPage
      title={title}
      subtitle={subtitle}
      actionLabel={createLabel}
      onCreate={createRoute ? () => navigation.navigate(createRoute) : undefined}
      searchPlaceholder={searchPlaceholder || `Search ${title.toLowerCase()}...`}
      rows={rowsWithSerial}
      columns={columns}
      emptyTitle={emptyTitle}
      stats={[
        { label: `Total ${title}`, value: safeRows.length, icon: ListChecks, tone: "blue" },
        { label: "Total Amount", value: totalKey ? money(amount) : safeRows.length, icon: IndianRupee, tone: "orange" },
        { label: "Active Records", value: safeRows.length, icon: Layers, tone: "green" },
        { label: "Needs Review", value: 0, icon: FileText, tone: "red" },
      ]}
      onDelete={handleDelete}
      onView={renderDetail || resolvedDocumentConfig ? setSelectedDocument : undefined}
      onEdit={renderEdit ? setEditingDocument : undefined}
    />
  );
};

export const resourceStatus = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("overdue") || status.includes("inactive")) return <StatusBadge tone="red">{value}</StatusBadge>;
  if (status.includes("paid") || status.includes("active")) return <StatusBadge tone="green">{value}</StatusBadge>;
  if (status.includes("draft")) return <StatusBadge tone="blue">{value}</StatusBadge>;
  return <StatusBadge tone="orange">{value || "-"}</StatusBadge>;
};

export const resourceMoney = money;

export default PremiumResourcePage;
